import React, { Component } from "react";
import ApiService from "../../_services/ApiService";
import "./test-dashboard.css";

interface TestDashboardProps {
  testId: number;
  testName: string;
  batchId?: number;
  lessonPlanId?: number;
  stepId?: number;
  onClose: () => void;
}

interface TestDashboardState {
  loading: boolean;
  error: string | null;
  data: any | null;
  activeTab: string;
  questionPage: number;
}

export class TestDashboard extends Component<TestDashboardProps, TestDashboardState> {
  questionsPerPage = 5;

  state: TestDashboardState = {
    loading: true,
    error: null,
    data: null,
    activeTab: "overview",
    questionPage: 0,
  };

  componentDidMount() {
    this.loadTestDashboard();
  }

  loadTestDashboard = async () => {
    const { testId, batchId, lessonPlanId, stepId } = this.props;

    this.setState({ loading: true, error: null });

    try {
      const response: any = await ApiService.getTestDashboardAnalytics({
        testId,
        batchId,
        lessonPlanId,
        stepId,
      });

      if (response.data && response.data.success) {
        this.setState({
          data: response.data.data,
          loading: false,
        });
      } else {
        this.setState({
          error: "Failed to load test dashboard data",
          loading: false,
        });
      }
    } catch (error: any) {
      console.error("Error loading test dashboard:", error);
      this.setState({
        error: error.response?.data?.message || "Failed to load test dashboard",
        loading: false,
      });
    }
  };

  switchTab = (tabName: string) => {
    this.setState({ activeTab: tabName });
  };

  nextQuestionPage = () => {
    const { data, questionPage } = this.state;
    const totalPages = Math.ceil(
      (data?.questionAnalytics?.length || 0) / this.questionsPerPage
    );
    if (questionPage < totalPages - 1) {
      this.setState({ questionPage: questionPage + 1 });
    }
  };

  prevQuestionPage = () => {
    const { questionPage } = this.state;
    if (questionPage > 0) {
      this.setState({ questionPage: questionPage - 1 });
    }
  };

  renderOverviewTab() {
    const { data } = this.state;
    if (!data) return null;

    const { testInfo, metrics } = data;

    return (
      <div className="test-overview-tab">
        {/* Test Info Card */}
        <div className="analytics-card test-info-card-compact">
          <div className="test-info-line">
            <span className="test-info-item">
              <strong>Test:</strong> {testInfo?.name || "N/A"}
            </span>
            <span className="test-info-separator">•</span>
            <span className="test-info-item">
              <strong>Total Marks:</strong> {testInfo?.totalMarks || 0}
            </span>
            <span className="test-info-separator">•</span>
            <span className="test-info-item">
              <strong>Total Questions:</strong> {testInfo?.totalQuestions || 0}
            </span>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="test-metrics-grid">
          <div className="metric-card">
            <div className="metric-icon">👥</div>
            <div className="metric-content">
              <div className="metric-label">Total Students</div>
              <div className="metric-value">{metrics?.totalStudents || 0}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">📝</div>
            <div className="metric-content">
              <div className="metric-label">Test Taken</div>
              <div className="metric-value">{metrics?.testTaken || 0}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">✅</div>
            <div className="metric-content">
              <div className="metric-label">Completed</div>
              <div className="metric-value">{metrics?.completed || 0}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">⏳</div>
            <div className="metric-content">
              <div className="metric-label">In Progress</div>
              <div className="metric-value">{metrics?.inProgress || 0}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">📊</div>
            <div className="metric-content">
              <div className="metric-label">Class Average</div>
              <div className="metric-value">{metrics?.classAverage?.toFixed(1) || 0}%</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">🎯</div>
            <div className="metric-content">
              <div className="metric-label">Test Average</div>
              <div className="metric-value">{metrics?.testAverage?.toFixed(1) || 0}%</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">⭐</div>
            <div className="metric-content">
              <div className="metric-label">80% Achievers</div>
              <div className="metric-value">{metrics?.achievers80Percent || 0}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">📈</div>
            <div className="metric-content">
              <div className="metric-label">Completion Rate</div>
              <div className="metric-value">
                {metrics?.testTaken > 0
                  ? Math.round((metrics.completed / metrics.testTaken) * 100)
                  : 0}
                %
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderQuestionAnalyticsTab() {
    const { data, questionPage } = this.state;
    if (!data?.questionAnalytics || data.questionAnalytics.length === 0) {
      return (
        <div className="alert alert-info">
          No question analytics available for this test
        </div>
      );
    }

    const startIdx = questionPage * this.questionsPerPage;
    const endIdx = Math.min(
      startIdx + this.questionsPerPage,
      data.questionAnalytics.length
    );
    const questions = data.questionAnalytics.slice(startIdx, endIdx);
    const totalPages = Math.ceil(data.questionAnalytics.length / this.questionsPerPage);

    return (
      <div className="question-analytics-tab">
        <div className="analytics-card">
          <h5>📊 Question-Wise Performance</h5>

          <div className="question-charts-grid">
            {questions.map((q: any, idx: number) => {
              const total = q.correctAttempts + q.wrongAttempts + q.notAttempted;
              const correctPct = total > 0 ? (q.correctAttempts / total) * 100 : 0;
              const wrongPct = total > 0 ? (q.wrongAttempts / total) * 100 : 0;
              const notAttemptedPct = total > 0 ? (q.notAttempted / total) * 100 : 0;

              return (
                <div key={idx} className="question-chart-card">
                  <h6>Q{q.questionNumber}</h6>
                  <div className="donut-chart">
                    <svg viewBox="0 0 36 36" className="circular-chart">
                      {/* Correct - Green */}
                      <path
                        className="circle correct"
                        strokeDasharray={`${correctPct}, 100`}
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      {/* Wrong - Red */}
                      <path
                        className="circle wrong"
                        strokeDasharray={`${wrongPct}, 100`}
                        strokeDashoffset={`-${correctPct}`}
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      {/* Not Attempted - Gray */}
                      <path
                        className="circle not-attempted"
                        strokeDasharray={`${notAttemptedPct}, 100`}
                        strokeDashoffset={`-${correctPct + wrongPct}`}
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <text x="18" y="20.35" className="percentage">
                        {q.accuracy}%
                      </text>
                    </svg>
                  </div>
                  <div className="question-stats">
                    <div className="stat correct">
                      <span className="stat-dot"></span>
                      Correct: {q.correctAttempts}
                    </div>
                    <div className="stat wrong">
                      <span className="stat-dot"></span>
                      Wrong: {q.wrongAttempts}
                    </div>
                    <div className="stat not-attempted">
                      <span className="stat-dot"></span>
                      Not Attempted: {q.notAttempted}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={this.prevQuestionPage}
                disabled={questionPage === 0}
              >
                ← Prev
              </button>
              <span className="pagination-info">
                Page {questionPage + 1} of {totalPages} | Total Questions:{" "}
                {data.questionAnalytics.length}
              </span>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={this.nextQuestionPage}
                disabled={questionPage >= totalPages - 1}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderStudentPerformanceTab() {
    const { data } = this.state;
    if (!data?.students || data.students.length === 0) {
      return (
        <div className="alert alert-info">No student performance data available</div>
      );
    }

    return (
      <div className="student-performance-tab">
        <div className="analytics-card">
          <h5>👥 Student Performance Details</h5>
          <div className="table-responsive">
            <table className="table table-hover analytics-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Status</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Marks</th>
                  <th>Percentage</th>
                  <th>Time Spent</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((student: any, idx: number) => (
                  <tr key={idx}>
                    <td>
                      <strong>{student.name || "Unknown"}</strong>
                      {student.code && <div className="text-muted">{student.code}</div>}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          student.status === "Completed"
                            ? "status-completed"
                            : student.status === "In Progress"
                              ? "status-in-progress"
                              : "status-not-started"
                        }`}
                      >
                        {student.status}
                      </span>
                    </td>
                    <td>{student.startTime}</td>
                    <td>{student.endTime}</td>
                    <td>
                      {student.marks} / {student.totalMarks}
                    </td>
                    <td>
                      <span
                        className={`score-badge ${
                          student.percentage >= 70
                            ? "score-good"
                            : student.percentage >= 50
                              ? "score-average"
                              : "score-poor"
                        }`}
                      >
                        {student.percentage.toFixed(1)}%
                      </span>
                    </td>
                    <td>{Math.round(student.timeSpent / 60)} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { testName, onClose } = this.props;
    const { loading, error, activeTab } = this.state;

    return (
      <div className="test-dashboard-modal-overlay">
        <div className="test-dashboard-modal">
          {/* Header */}
          <div className="test-dashboard-header">
            <h2>📊 Test Dashboard - {testName}</h2>
            <button className="btn-close-dashboard" onClick={onClose}>
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="test-dashboard-body">
            {loading && (
              <div className="analytics-loading">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p>Loading test dashboard...</p>
              </div>
            )}

            {error && (
              <div className="alert alert-danger">
                <strong>Error:</strong> {error}
                <button
                  className="btn btn-sm btn-outline-danger ml-3"
                  onClick={this.loadTestDashboard}
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && (
              <>
                {/* Tab Navigation */}
                <div className="test-dashboard-tabs">
                  <button
                    className={`dashboard-tab ${activeTab === "overview" ? "active" : ""}`}
                    onClick={() => this.switchTab("overview")}
                  >
                    📋 Overview
                  </button>
                  <button
                    className={`dashboard-tab ${activeTab === "questions" ? "active" : ""}`}
                    onClick={() => this.switchTab("questions")}
                  >
                    📊 Question Analytics
                  </button>
                  <button
                    className={`dashboard-tab ${activeTab === "students" ? "active" : ""}`}
                    onClick={() => this.switchTab("students")}
                  >
                    👥 Student Performance
                  </button>
                </div>

                {/* Tab Content */}
                <div className="test-dashboard-content">
                  {activeTab === "overview" && this.renderOverviewTab()}
                  {activeTab === "questions" && this.renderQuestionAnalyticsTab()}
                  {activeTab === "students" && this.renderStudentPerformanceTab()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}
